import { IconType } from "react-icons";
import {
  MdOutlineSportsSoccer,
  MdSports,
  MdSportsBaseball,
  MdSportsBasketball,
  MdSportsCricket,
  MdSportsFootball,
  MdSportsHandball,
  MdSportsHockey,
  MdSportsMma,
  MdSportsMotorsports,
  MdSportsRugby,
  MdSportsTennis,
  MdSportsVolleyball,
  MdOutlineSportsMma,
} from "react-icons/md";

/**
 * Sport ids as the API serves them (`sports.bb_id`, which is also `sports.id`).
 * These are the BetBalancer / SportRadar ids; every backend keeps them.
 */
export const SPORT_IDS = {
  soccer: "1",
  basketball: "2",
  baseball: "3",
  iceHockey: "4",
  tennis: "5",
  handball: "6",
  boxing: "10",
  rugby: "12",
  aussieRules: "13",
  americanFootball: "16",
  tableTennis: "20",
  cricket: "21",
  darts: "22",
  volleyball: "23",
  futsal: "29",
  badminton: "31",
  formula1: "40",
  mma: "117",
} as const;

/** A sport row from `/v4/sports` (`state.Sports.Index.data[]`). */
export interface ApiSport {
  id?: string | number;
  bb_id?: string | number;
  _id?: string | number;
  name?: string;
  [key: string]: unknown;
}

/** One sport ready for a menu: label, icon and link. */
export interface SportMenuItem {
  id: string;
  title: string;
  Icon: IconType;
  href: string;
}

interface SportMeta {
  title: string;
  Icon: IconType;
}

/**
 * Display label and icon per sport id. The order here is the menu order;
 * sports the API sends that are not listed here follow, in API order.
 */
const SPORT_META: Array<[string, SportMeta]> = [
  [SPORT_IDS.soccer, { title: "Football", Icon: MdOutlineSportsSoccer }],
  [SPORT_IDS.basketball, { title: "Basketball", Icon: MdSportsBasketball }],
  [SPORT_IDS.boxing, { title: "Boxing", Icon: MdOutlineSportsMma }],
  [SPORT_IDS.volleyball, { title: "Volleyball", Icon: MdSportsVolleyball }],
  [SPORT_IDS.tennis, { title: "Tennis", Icon: MdSportsTennis }],
  [SPORT_IDS.tableTennis, { title: "Table Tennis", Icon: MdSportsTennis }],
  [SPORT_IDS.baseball, { title: "Baseball", Icon: MdSportsBaseball }],
  [SPORT_IDS.iceHockey, { title: "Ice Hockey", Icon: MdSportsHockey }],
  [SPORT_IDS.handball, { title: "Handball", Icon: MdSportsHandball }],
  [SPORT_IDS.americanFootball, { title: "American Football", Icon: MdSportsFootball }],
  [SPORT_IDS.rugby, { title: "Rugby", Icon: MdSportsRugby }],
  [SPORT_IDS.mma, { title: "MMA", Icon: MdSportsMma }],
  [SPORT_IDS.formula1, { title: "Formula 1", Icon: MdSportsMotorsports }],
  [SPORT_IDS.cricket, { title: "Cricket", Icon: MdSportsCricket }],
  [SPORT_IDS.darts, { title: "Darts", Icon: MdSports }],
  [SPORT_IDS.badminton, { title: "Badminton", Icon: MdSports }],
  [SPORT_IDS.aussieRules, { title: "Aussie Rules", Icon: MdSports }],
  [SPORT_IDS.futsal, { title: "Futsal", Icon: MdOutlineSportsSoccer }],
];

const META_BY_ID = new Map<string, SportMeta>(SPORT_META);
const ORDER_BY_ID = new Map<string, number>(SPORT_META.map(([id], i) => [id, i]));

/** Icon for a sport the table above does not know. */
export const GenericSportIcon = MdSports;

const EMPTY: ApiSport[] = [];

/**
 * Redux selector for the sport list `/v4/sports` fills. Returns a stable
 * empty array while the list is loading, so it never breaks memoisation.
 *
 * @param {any} state Redux root state.
 * @returns {ApiSport[]}
 */
export const selectSports = (state: any): ApiSport[] => {
  const data = state?.Sports?.Index?.data;
  return Array.isArray(data) ? data : EMPTY;
};

/**
 * The id a sport is linked and matched by (`bb_id`, falling back to `id`).
 *
 * @param {ApiSport} sport
 * @returns {string} Empty string when the row has no id.
 */
export const getSportId = (sport: ApiSport | null | undefined): string => {
  const id = sport?.bb_id ?? sport?.id;
  return id === undefined || id === null ? "" : String(id);
};

/**
 * Display label for a sport: the known label for its id, else the API name.
 *
 * @param {ApiSport} sport
 * @returns {string}
 */
export const getSportLabel = (sport: ApiSport | null | undefined): string =>
  META_BY_ID.get(getSportId(sport))?.title ?? String(sport?.name ?? "");

/**
 * Icon for a sport: the known icon for its id, else a generic one.
 *
 * @param {ApiSport} sport
 * @returns {IconType}
 */
export const getSportIcon = (
  sport: ApiSport | null | undefined
): IconType =>
  META_BY_ID.get(getSportId(sport))?.Icon ?? GenericSportIcon;

/**
 * Route to a sport's detail page, in the format every menu uses.
 *
 * @param {string} sportId
 * @returns {string}
 */
export const sportDetailsHref = (sportId: string | number): string =>
  `/sports-details?id=${sportId}`;

/**
 * Turn the API sport list into menu items: rows without an id are dropped,
 * known sports come first in a fixed order, unknown ones follow in API order.
 *
 * @param {ApiSport[]} sports
 * @returns {SportMenuItem[]}
 */
export const buildSportMenuItems = (
  sports: ApiSport[] | null | undefined
): SportMenuItem[] => {
  if (!Array.isArray(sports) || sports.length === 0) return [];

  const unknownRank = SPORT_META.length;

  return sports
    .map((sport, apiIndex) => ({ sport, apiIndex, id: getSportId(sport) }))
    .filter(({ id }) => id !== "")
    .sort((a, b) => {
      const rankA = ORDER_BY_ID.get(a.id) ?? unknownRank;
      const rankB = ORDER_BY_ID.get(b.id) ?? unknownRank;
      return rankA !== rankB ? rankA - rankB : a.apiIndex - b.apiIndex;
    })
    .map(({ sport, id }) => ({
      id,
      title: getSportLabel(sport) || id,
      Icon: getSportIcon(sport),
      href: sportDetailsHref(id),
    }));
};
